import React, { useState, useEffect, useCallback } from 'react';
import {
  QrCode, Download, Loader2, Heart, CalendarDays, ShoppingBag,
  Camera, ExternalLink, RefreshCw, Info, Store, Copy, Check,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QRCard = ({ title, description, qrData, loading, onGenerate, onDownload, icon, linkUrl }) => (
  <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg" data-testid={`qr-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <h3 className="font-heading text-sm font-bold text-[#0A0A0A]">{title}</h3>
    </div>
    <p className="text-[10px] text-[#4B5563] mb-4">{description}</p>

    {!qrData && !loading && (
      <button onClick={onGenerate} className="btn-primary text-xs" data-testid={`generate-qr-${title.toLowerCase().replace(/\s/g, '-')}`}>
        Generate QR Code
      </button>
    )}

    {loading && (
      <div className="flex items-center gap-2 py-4 justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" />
        <span className="text-xs text-[#4B5563]">Generating...</span>
      </div>
    )}

    {qrData && (
      <div className="space-y-3">
        <div className="bg-[#F8F9FA] p-3 rounded-lg flex items-center justify-center">
          <img src={qrData.image || qrData.qr_image} alt={`${title} QR`} className="w-44 h-44" />
        </div>
        {linkUrl && (
          <a href={linkUrl} target="_blank" rel="noopener noreferrer"
            className="text-xs text-[#BA1826] font-medium hover:underline flex items-center gap-1 justify-center"
            data-testid={`qr-link-${title.toLowerCase().replace(/\s/g, '-')}`}>
            <ExternalLink className="w-3 h-3" /> {qrData.site_url || qrData.shop_url || qrData.upload_url || linkUrl}
          </a>
        )}
        <div className="flex gap-2">
          <button onClick={onDownload} className="btn-primary text-xs flex-1 flex items-center justify-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> Download
          </button>
          <button onClick={onGenerate} className="text-xs px-3 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>
    )}
  </div>
);

const AdminQrCodesTab = () => {
  // Site-level QR codes
  const [donateQr, setDonateQr] = useState(null);
  const [eventsQr, setEventsQr] = useState(null);
  const [shopQr, setShopQr] = useState(null);
  const [loadingType, setLoadingType] = useState(null);

  // Per-event QRs
  const [events, setEvents] = useState([]);
  const [eventQrs, setEventQrs] = useState({});
  const [eventQrLoading, setEventQrLoading] = useState(null);

  // Gallery QRs
  const [galleries, setGalleries] = useState([]);
  const [galleryQrs, setGalleryQrs] = useState({});
  const [galleryQrLoading, setGalleryQrLoading] = useState(null);
  const [reusableQr, setReusableQr] = useState({ enabled: false, active_gallery_id: null });
  const [reusableQrImage, setReusableQrImage] = useState(null);
  const [reusableQrLoading, setReusableQrLoading] = useState(false);

  // Pop-Up Shop QRs
  const [popupShops, setPopupShops] = useState([]);
  const [popupQrs, setPopupQrs] = useState({});
  const [popupQrLoading, setPopupQrLoading] = useState(null);

  // Event Pop-Up Shop (single permanent QR + global toggle)
  const [eventPopupQr, setEventPopupQr] = useState(null);   // { qr_image, url, active }
  const [eventPopupLoading, setEventPopupLoading] = useState(false);
  const [eventPopupToggleLoading, setEventPopupToggleLoading] = useState(false);
  const [eventPopupCopied, setEventPopupCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, galleriesRes, qrConfigRes, popupsRes, eventPopupRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/events`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/galleries`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/galleries/reusable-qr/config`, { withCredentials: true }).catch(() => ({ data: { enabled: false, active_gallery_id: null } })),
        axios.get(`${BACKEND_URL}/api/admin/popup-shops`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/popup-shop/qr`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setEvents(eventsRes.data);
      setGalleries(galleriesRes.data);
      setReusableQr(qrConfigRes.data);
      setPopupShops(popupsRes.data);
      if (eventPopupRes.data) setEventPopupQr(eventPopupRes.data);
    } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Site-level QR handlers
  const fetchSiteQr = async (type) => {
    setLoadingType(type);
    try {
      if (type === 'shop') {
        const res = await axios.get(`${BACKEND_URL}/api/admin/shop/qr`, { withCredentials: true });
        setShopQr(res.data);
      } else {
        const res = await axios.get(`${BACKEND_URL}/api/admin/qr-code/preview?type=${type}`, { withCredentials: true });
        if (type === 'events') setEventsQr(res.data);
        else setDonateQr(res.data);
      }
    } catch { alert('QR generation failed'); }
    setLoadingType(null);
  };

  const downloadSiteQr = (type) => {
    const qrData = type === 'events' ? eventsQr : type === 'shop' ? shopQr : donateQr;
    if (!qrData) return;
    const link = document.createElement('a');
    link.href = qrData.image || qrData.qr_image;
    link.download = `NHF_${type}_QR.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Per-event QR
  const generateEventQr = async (eventId) => {
    setEventQrLoading(eventId);
    try {
      const eventUrl = `${window.location.origin}/events/${eventId}`;
      const res = await axios.post(`${BACKEND_URL}/api/admin/qr-code`, { url: eventUrl }, { withCredentials: true });
      setEventQrs(prev => ({ ...prev, [eventId]: res.data.qr_code_base64 }));
    } catch { alert('Failed to generate event QR'); }
    setEventQrLoading(null);
  };

  const downloadEventQr = (eventId) => {
    const data = eventQrs[eventId];
    if (!data) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${data}`;
    link.download = `NHF_Event_QR_${eventId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Gallery QR
  const generateGalleryQr = async (galleryId) => {
    setGalleryQrLoading(galleryId);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/galleries/${galleryId}/qr`, { withCredentials: true });
      setGalleryQrs(prev => ({ ...prev, [galleryId]: res.data }));
    } catch { alert('Failed to generate gallery QR'); }
    setGalleryQrLoading(null);
  };

  const downloadGalleryQr = (galleryId) => {
    const data = galleryQrs[galleryId];
    if (!data) return;
    const link = document.createElement('a');
    link.href = data.qr_image;
    link.download = `NHF_Gallery_QR_${galleryId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reusable Gallery QR
  const handleReusableQrUpdate = async (update) => {
    try {
      const newConfig = { ...reusableQr, ...update };
      const res = await axios.put(`${BACKEND_URL}/api/admin/galleries/reusable-qr/config`, newConfig, { withCredentials: true });
      setReusableQr(res.data);
    } catch { alert('Failed to update reusable QR config'); }
  };

  const showReusableQr = async () => {
    setReusableQrLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/galleries/reusable-qr/image`, { withCredentials: true });
      setReusableQrImage(res.data);
    } catch { alert('Failed to generate reusable QR'); }
    setReusableQrLoading(false);
  };

  // Pop-Up Shop QR
  const generatePopupQr = async (popupId) => {
    setPopupQrLoading(popupId);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/popup-shops/${popupId}/qr`, { withCredentials: true });
      setPopupQrs(prev => ({ ...prev, [popupId]: res.data }));
    } catch { alert('Failed to generate pop-up QR'); }
    setPopupQrLoading(null);
  };

  const downloadPopupQr = (popupId, slug) => {
    const data = popupQrs[popupId];
    if (!data) return;
    const link = document.createElement('a');
    link.href = data.qr_image || data.qr_data_url;
    link.download = `NHF_Popup_QR_${slug}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ---- Event Pop-Up Shop (single permanent QR + active toggle) ---------------
  const refreshEventPopupQr = async () => {
    setEventPopupLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/popup-shop/qr`, { withCredentials: true });
      setEventPopupQr(r.data);
    } catch { alert('Failed to load Event Pop-Up QR'); }
    setEventPopupLoading(false);
  };

  const toggleEventPopupActive = async () => {
    if (!eventPopupQr) return;
    const next = !eventPopupQr.active;
    if (!next && !window.confirm('Turn OFF the Event Pop-Up Shop?\n\nScanning the QR will show "Pop-up shop is not active right now." until you turn it back on.')) return;
    setEventPopupToggleLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/popup-shop/config`,
        { active: next },
        { withCredentials: true });
      setEventPopupQr(prev => ({ ...prev, active: next }));
    } catch { alert('Failed to update Pop-Up Shop status'); }
    setEventPopupToggleLoading(false);
  };

  const downloadEventPopupQr = () => {
    if (!eventPopupQr?.qr_image) return;
    const link = document.createElement('a');
    link.href = eventPopupQr.qr_image;
    link.download = `NHF_Event_PopUp_Shop_QR.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyEventPopupUrl = async () => {
    if (!eventPopupQr?.url) return;
    try {
      await navigator.clipboard.writeText(eventPopupQr.url);
      setEventPopupCopied(true);
      setTimeout(() => setEventPopupCopied(false), 1800);
    } catch { alert('Could not copy URL'); }
  };

  const publishedEvents = events.filter(e => e.is_published);

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="qr-codes-tab">
      {/* Header */}
      <div className="text-center">
        <QrCode className="w-10 h-10 text-[#BA1826] mx-auto mb-3" />
        <h2 className="font-heading text-xl font-bold text-[#0A0A0A]">QR Codes</h2>
        <p className="text-sm text-[#4B5563] mt-1">Generate and download all branded QR codes from one place</p>
      </div>

      {/* ========== SITE-LEVEL QR CODES ========== */}
      <div>
        <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-3 uppercase tracking-wide">Site Pages</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <QRCard
            title="Donation Page"
            description="Links to /donate — for flyers, business cards, and social media."
            qrData={donateQr}
            loading={loadingType === 'donate'}
            onGenerate={() => fetchSiteQr('donate')}
            onDownload={() => downloadSiteQr('donate')}
            icon={<Heart className="w-4 h-4 text-[#BA1826]" />}
            linkUrl={donateQr ? donateQr.site_url : null}
          />
          <QRCard
            title="Events Page"
            description="Links to /events — share at fundraisers for RSVPs."
            qrData={eventsQr}
            loading={loadingType === 'events'}
            onGenerate={() => fetchSiteQr('events')}
            onDownload={() => downloadSiteQr('events')}
            icon={<CalendarDays className="w-4 h-4 text-[#BA1826]" />}
            linkUrl={eventsQr ? eventsQr.site_url : null}
          />
          <QRCard
            title="Shop"
            description="Links to /shop — print for events so guests can browse merch."
            qrData={shopQr ? { image: shopQr.qr_image, site_url: shopQr.shop_url } : null}
            loading={loadingType === 'shop'}
            onGenerate={() => fetchSiteQr('shop')}
            onDownload={() => downloadSiteQr('shop')}
            icon={<ShoppingBag className="w-4 h-4 text-[#BA1826]" />}
            linkUrl={shopQr ? shopQr.shop_url : null}
          />

          {/* Event Pop-Up Shop — permanent QR + active toggle, sits next to Shop */}
          <div className="bg-white p-5 border border-[#E5E7EB] rounded-lg" data-testid="event-popup-shop-qr-card">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-[#BA1826]" />
                <h3 className="font-heading text-sm font-bold text-[#0A0A0A]">Event Pop-Up Shop QR</h3>
              </div>
              {eventPopupQr && (
                <span
                  className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${
                    eventPopupQr.active
                      ? 'bg-[#DCFCE7] text-[#166534]'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                  data-testid="event-popup-active-badge"
                >
                  {eventPopupQr.active ? 'Active' : 'Inactive'}
                </span>
              )}
            </div>
            <p className="text-[10px] text-[#4B5563] mb-4">
              Permanent QR for in-person fundraiser checkout. Pulls from the regular shop inventory; orders ship-free, in-person pickup.
            </p>

            {!eventPopupQr && !eventPopupLoading && (
              <button onClick={refreshEventPopupQr} className="btn-primary text-xs" data-testid="event-popup-load-btn">
                Load QR
              </button>
            )}
            {eventPopupLoading && (
              <div className="flex items-center gap-2 py-4 justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-[#BA1826]" />
                <span className="text-xs text-[#4B5563]">Loading...</span>
              </div>
            )}
            {eventPopupQr && (
              <div className="space-y-3">
                <div className="bg-[#F8F9FA] p-3 rounded-lg flex items-center justify-center">
                  <img src={eventPopupQr.qr_image} alt="Event Pop-Up Shop QR" className="w-44 h-44" data-testid="event-popup-qr-image" />
                </div>

                <div className="flex items-center gap-2 text-xs">
                  <a href={eventPopupQr.url} target="_blank" rel="noopener noreferrer"
                    className="text-[#BA1826] font-medium hover:underline flex items-center gap-1 flex-1 min-w-0 truncate"
                    data-testid="event-popup-url-link">
                    <ExternalLink className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{eventPopupQr.url}</span>
                  </a>
                  <button onClick={copyEventPopupUrl}
                    className="text-xs px-2 py-1 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1 flex-shrink-0"
                    data-testid="event-popup-copy-url-btn"
                    title="Copy URL">
                    {eventPopupCopied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
                    {eventPopupCopied ? 'Copied' : 'Copy'}
                  </button>
                </div>

                {/* Active toggle */}
                <label className="flex items-center justify-between gap-2 px-3 py-2 bg-[#F8F9FA] rounded border border-[#E5E7EB] cursor-pointer">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#0A0A0A]">Pop-Up Shop Active</p>
                    <p className="text-[10px] text-[#9CA3AF]">
                      {eventPopupQr.active
                        ? 'Scanning the QR opens the checkout.'
                        : 'Scanning shows "Pop-up shop is not active right now."'}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={!!eventPopupQr.active}
                    onChange={toggleEventPopupActive}
                    disabled={eventPopupToggleLoading}
                    className="w-4 h-4 accent-[#BA1826] cursor-pointer flex-shrink-0"
                    data-testid="event-popup-active-toggle"
                  />
                </label>

                <div className="grid grid-cols-3 gap-1.5">
                  <button onClick={downloadEventPopupQr}
                    className="btn-primary text-xs flex items-center justify-center gap-1"
                    data-testid="event-popup-download-btn">
                    <Download className="w-3 h-3" /> Download
                  </button>
                  <a href={eventPopupQr.url} target="_blank" rel="noopener noreferrer"
                    className="text-xs px-2 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center justify-center gap-1"
                    data-testid="event-popup-preview-btn">
                    <ExternalLink className="w-3 h-3" /> Preview
                  </a>
                  <button onClick={refreshEventPopupQr}
                    className="text-xs px-2 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center justify-center gap-1"
                    data-testid="event-popup-refresh-btn">
                    <RefreshCw className="w-3 h-3" /> Refresh
                  </button>
                </div>
                <a href="/admin/popup-live" target="_blank" rel="noopener noreferrer"
                  className="block w-full text-center text-xs font-bold uppercase tracking-wide px-3 py-2 rounded bg-[#0A0A0A] text-white hover:bg-[#1F1F1F]"
                  data-testid="open-popup-live-kiosk-btn">
                  Open Live Mode Kiosk →
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ========== PER-EVENT QR CODES ========== */}
      <div>
        <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-3 uppercase tracking-wide">Per-Event QR Codes</h3>
        {publishedEvents.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] bg-white border border-[#E5E7EB] rounded-lg p-4">No published events. Create and publish an event first.</p>
        ) : (
          <div className="space-y-3">
            {publishedEvents.map(ev => (
              <div key={ev.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-center justify-between flex-wrap gap-3"
                data-testid={`event-qr-row-${ev.id}`}>
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading text-xs font-bold text-[#0A0A0A] truncate">{ev.title}</h4>
                  <p className="text-[10px] text-[#4B5563]">{ev.date || 'No date'} — /events/{ev.id}</p>
                </div>
                <div className="flex items-center gap-2">
                  {eventQrs[ev.id] ? (
                    <>
                      <img src={`data:image/png;base64,${eventQrs[ev.id]}`} alt="QR" className="w-12 h-12 rounded border border-[#E5E7EB]" />
                      <button onClick={() => downloadEventQr(ev.id)}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1"
                        data-testid={`download-event-qr-${ev.id}`}>
                        <Download className="w-3 h-3" /> Download
                      </button>
                      <button onClick={() => generateEventQr(ev.id)}
                        className="text-xs px-2 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA]">
                        <RefreshCw className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <button onClick={() => generateEventQr(ev.id)} disabled={eventQrLoading === ev.id}
                      className="text-xs font-semibold px-3 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1 disabled:opacity-50"
                      data-testid={`gen-event-qr-${ev.id}`}>
                      {eventQrLoading === ev.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                      Generate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* (Per-pop-up QR section retired — superseded by the single permanent Event Pop-Up Shop QR
          shown in the Site Pages grid above. Existing pop-up shops remain manageable via the
          Pop-Up Shops tab, but use the global QR for in-person events going forward.) */}

      {/* ========== GALLERY QR CODES ========== */}
      <div>
        <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-3 uppercase tracking-wide">Gallery QR Codes</h3>

        {/* Walk-through */}
        <div className="bg-[#FEF9E7] border border-[#F4D03F] rounded-lg p-3 flex items-start gap-2 mb-4" data-testid="gallery-qr-walkthrough">
          <Info className="w-4 h-4 text-[#BA1826] flex-shrink-0 mt-0.5" />
          <div className="text-xs text-[#4B5563]">
            <strong className="text-[#0A0A0A]">Gallery QR walk-through:</strong> Use the <strong>Reusable QR</strong> (one permanent QR that you can point at any active gallery) to print once and re-use at every event — just switch which gallery it targets. Or generate a <strong>Per-Event QR</strong> below for a single gallery that never changes. Both let guests scan and upload photos to the right gallery instantly.
          </div>
        </div>

        {/* Reusable Gallery QR */}
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-4" data-testid="reusable-gallery-qr">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-heading text-xs font-bold text-[#0A0A0A] flex items-center gap-2">
                <Camera className="w-3.5 h-3.5 text-[#BA1826]" /> Reusable Gallery QR
              </h4>
              <p className="text-[10px] text-[#4B5563] mt-0.5">One permanent QR — assign it to any active gallery</p>
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
              <input type="checkbox" checked={reusableQr.enabled}
                onChange={(e) => handleReusableQrUpdate({ enabled: e.target.checked })}
                className="accent-[#BA1826]" data-testid="reusable-qr-toggle" />
              {reusableQr.enabled ? 'On' : 'Off'}
            </label>
          </div>
          {reusableQr.enabled && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="text-[10px] font-medium text-[#4B5563] mb-1 block">Active Gallery</label>
                <select value={reusableQr.active_gallery_id || ''}
                  onChange={(e) => handleReusableQrUpdate({ active_gallery_id: e.target.value || null })}
                  className="w-full px-3 py-2 text-xs border border-[#E5E7EB] rounded-md focus:border-[#BA1826] focus:outline-none"
                  data-testid="reusable-qr-gallery-select">
                  <option value="">None (QR inactive)</option>
                  {galleries.map(g => (
                    <option key={g.id} value={g.id}>
                      📸 {g.event_title} — {g.event_date || 'no date'} [{g.status}]
                    </option>
                  ))}
                </select>
                {reusableQr.active_gallery_id && (() => {
                  const g = galleries.find(x => x.id === reusableQr.active_gallery_id);
                  if (!g) return null;
                  return (
                    <p className="text-[10px] text-green-700 mt-1 flex items-center gap-1" data-testid="reusable-qr-active-event">
                      <Camera className="w-2.5 h-2.5" /> Scanning this QR now uploads to: <strong>{g.event_title}</strong>
                    </p>
                  );
                })()}
              </div>
              <button onClick={showReusableQr} disabled={reusableQrLoading}
                className="text-xs font-semibold px-3 py-2 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1 disabled:opacity-50"
                data-testid="show-reusable-qr-btn">
                {reusableQrLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                View / Download
              </button>
            </div>
          )}
          {reusableQrImage && (
            <div className="mt-4 bg-[#F8F9FA] rounded-lg p-4 text-center">
              <img src={reusableQrImage.qr_image} alt="Reusable QR" className="w-44 h-44 mx-auto mb-2" />
              <p className="text-[10px] text-[#9CA3AF] mb-2">{reusableQrImage.upload_url}</p>
              <a href={reusableQrImage.qr_image} download="NHF_Reusable_Gallery_QR.png"
                className="btn-primary text-xs inline-flex items-center gap-1.5" data-testid="download-reusable-qr">
                <Download className="w-3.5 h-3.5" /> Download
              </a>
            </div>
          )}
        </div>

        {/* Per-Gallery QR Codes */}
        {galleries.length === 0 ? (
          <p className="text-xs text-[#9CA3AF] bg-white border border-[#E5E7EB] rounded-lg p-4">No galleries created yet.</p>
        ) : (
          <div className="space-y-3">
            {galleries.map(g => (
              <div key={g.id} className="bg-white border border-[#E5E7EB] rounded-lg p-4 flex items-center justify-between flex-wrap gap-3"
                data-testid={`gallery-qr-row-${g.id}`}>
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading text-xs font-bold text-[#0A0A0A] truncate">📸 {g.event_title}</h4>
                  <p className="text-[10px] text-[#4B5563]">{g.event_date || 'no date'} · status: <strong>{g.status}</strong> · /gallery/{g.id}/upload</p>
                </div>
                <div className="flex items-center gap-2">
                  {galleryQrs[g.id] ? (
                    <>
                      <img src={galleryQrs[g.id].qr_image} alt="QR" className="w-12 h-12 rounded border border-[#E5E7EB]" />
                      <button onClick={() => downloadGalleryQr(g.id)}
                        className="text-xs font-semibold px-2.5 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1"
                        data-testid={`download-gallery-qr-${g.id}`}>
                        <Download className="w-3 h-3" /> Download
                      </button>
                      <button onClick={() => generateGalleryQr(g.id)}
                        className="text-xs px-2 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA]">
                        <RefreshCw className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <button onClick={() => generateGalleryQr(g.id)} disabled={galleryQrLoading === g.id}
                      className="text-xs font-semibold px-3 py-1.5 rounded border border-[#E5E7EB] text-[#4B5563] hover:bg-[#F8F9FA] flex items-center gap-1 disabled:opacity-50"
                      data-testid={`gen-gallery-qr-${g.id}`}>
                      {galleryQrLoading === g.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                      Generate
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Usage Tips */}
      <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-lg">
        <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">Printing Tips</h4>
        <ul className="text-xs text-[#4B5563] space-y-1">
          <li>- Print at minimum 1 inch (2.5 cm) for reliable scanning</li>
          <li>- The heart logo in the center is your brand identifier</li>
          <li>- Works on flyers, posters, business cards, and social media</li>
          <li>- Red border matches the Novena Foundation brand colors</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminQrCodesTab;
